import { create } from 'zustand';
import { APP_VERSION, BUILD_DATE } from '@/generated-version';

/**
 * @fileoverview Version store (Zustand)
 * @description
 * React/Zustand port of the Angular `VersionCheckService`. Compares the bundled
 * app version (`APP_VERSION` from `@/generated-version`) against the deployed
 * `/version.json` to detect updates. Signal names (`updateAvailable`,
 * `currentVersion`, `remoteVersion`), method names (`checkForUpdate`,
 * `clearCacheAndReload`), the localStorage key (`app_version`), and the
 * semantic-version comparison logic are reproduced 1:1.
 *
 * Adaptation notes:
 * - Angular read the bundle version from an `APP_VERSION_INFO` injection token
 *   (package.json). In React the build-time generated module `@/generated-version`
 *   supplies `APP_VERSION` and `BUILD_DATE`; buildNumber has no React equivalent
 *   and is reproduced as '0' (matching the Angular token factory default).
 * - Angular fetched `/version.json` via HttpClient + firstValueFrom; replaced
 *   with `fetch` (same URL, cache-busting query + no-cache headers).
 */

export interface VersionInfo {
  version: string;
  buildDate: string;
  buildNumber: string;
}

/** Bundled version info (was the APP_VERSION_INFO token factory). */
const localVersionInfo: VersionInfo = {
  version: APP_VERSION,
  buildDate: BUILD_DATE,
  buildNumber: '0',
};

const STORAGE_KEY = 'app_version';

interface VersionState {
  /** Whether an update is available */
  updateAvailable: boolean;
  /** Current local version info (the "old" version used for comparison) */
  currentVersion: VersionInfo | null;
  /** Remote/deployed version info */
  remoteVersion: VersionInfo | null;

  checkForUpdate: () => Promise<boolean>;
  clearCacheAndReload: () => Promise<void>;
}

/** Get the previously stored version from localStorage. */
function getStoredVersion(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch (error) {
    console.warn('Failed to read stored version:', error);
    return null;
  }
}

/** Store the current version in localStorage for next visit. */
function storeVersion(version: string): void {
  try {
    localStorage.setItem(STORAGE_KEY, version);
  } catch (error) {
    console.warn('Failed to store version:', error);
  }
}

/** Fetch remote version info from the deployed server (cache-busted). */
async function fetchRemoteVersion(): Promise<VersionInfo> {
  const timestamp = Date.now();
  const response = await fetch(`/version.json?t=${timestamp}`, {
    headers: {
      'Cache-Control': 'no-cache',
      Pragma: 'no-cache',
    },
  });
  return (await response.json()) as VersionInfo;
}

/**
 * Compare two semantic version strings.
 * @returns -1 if v1 < v2, 0 if equal, 1 if v1 > v2
 */
function compareVersions(v1: string, v2: string): number {
  const v1Parts = v1.replace(/^v/, '').split('.').map(Number);
  const v2Parts = v2.replace(/^v/, '').split('.').map(Number);
  const maxLength = Math.max(v1Parts.length, v2Parts.length);

  for (let i = 0; i < maxLength; i++) {
    const v1Part = v1Parts[i] || 0;
    const v2Part = v2Parts[i] || 0;
    if (v1Part > v2Part) return 1;
    if (v1Part < v2Part) return -1;
  }
  return 0;
}

export const useVersionStore = create<VersionState>((set, get) => ({
  updateAvailable: false,
  currentVersion: null,
  remoteVersion: null,

  checkForUpdate: async () => {
    try {
      const currentBundleVersion = localVersionInfo.version;
      const storedVersion = getStoredVersion();

      const remoteVersion = await fetchRemoteVersion();
      set({ remoteVersion });

      // Determine which version to use as "old" version.
      const oldVersion = storedVersion || currentBundleVersion;

      const oldVersionInfo: VersionInfo = {
        version: oldVersion,
        buildDate: localVersionInfo.buildDate,
        buildNumber: localVersionInfo.buildNumber,
      };
      set({ currentVersion: oldVersionInfo });

      const comparison = compareVersions(oldVersion, remoteVersion.version);
      const isUpdateAvailable = comparison < 0;
      set({ updateAvailable: isUpdateAvailable });

      if (!isUpdateAvailable) {
        // Store current version for next visit.
        storeVersion(currentBundleVersion);
      }

      return isUpdateAvailable;
    } catch (error) {
      console.error('Version check failed:', error);
      // Don't block app if version check fails.
      return false;
    }
  },

  clearCacheAndReload: async () => {
    try {
      // Update stored version to new version BEFORE reload (prevents update loop).
      const newVersion = get().remoteVersion?.version;
      if (newVersion) {
        storeVersion(newVersion);
      }

      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map((cacheName) => caches.delete(cacheName)));
      }

      sessionStorage.clear();
      // Note: we preserve localStorage intentionally.

      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        await Promise.all(registrations.map((registration) => registration.unregister()));
      }

      window.location.reload();
    } catch (error) {
      console.error('Failed to clear cache:', error);
      window.location.reload();
    }
  },
}));
